import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`
  }} data-id="element-652" data-mp="3icvkmthqqaj1">
      {children}
    </div>;
};
const Counter = ({
  end,
  duration = 1500,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.4
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [started, end, duration]);
  return <span ref={ref} data-id="element-653" data-mp="3w3otxlyrd8ni">
      {value}
      {suffix}
    </span>;
};
export function AboutPage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const stats = [{
    value: 400,
    suffix: en ? 'kg' : ru ? 'кг' : 'kg',
    label: en ? 'Max capacity' : ru ? 'Макс. грузоподъемность' : 'Maks. nosivost'
  }, {
    text: en ? 'Same day' : ru ? 'В тот же день' : 'Isti dan',
    label: en ? 'From stock' : ru ? 'Со склада' : 'Sa lagera'
  }, {
    value: 1,
    suffix: '',
    label: en ? 'Year warranty' : ru ? 'Год гарантии' : 'Godina garancije'
  }, {
    value: 4,
    suffix: '',
    label: en ? 'Models available' : ru ? 'Доступных моделей' : 'Dostupna modela'
  }];
  return <main className="min-h-screen bg-dark-bg py-24" data-id="element-654" data-mp="2pi4f3h6ianpd">
      <div className="max-w-[700px] mx-auto px-5 sm:px-6" data-id="element-655" data-mp="14ztpb5qi9zep">
        <FadeIn data-id="element-656" data-mp="151mueglh0bkl">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-657" data-mp="190vq3r02mjis">
            {en ? 'About us: ' : ru ? 'О нас: ' : 'O nama: '}
            <span className="text-brand-yellow" data-id="element-658" data-mp="1e0i1duwc9poy">D-TECH</span>
          </h1>
          <div className="w-16 h-1 bg-brand-yellow rounded-full mb-12" data-id="element-659" data-mp="1izbcjgo5l4ly" />
        </FadeIn>

        <FadeIn delay={50} data-id="element-660" data-mp="lbt5qbe1yckl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-661" data-mp="lcm9d3hpslyi">
            {stats.map((stat, i) => <div key={i} className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center hover:border-brand-yellow/60 transition-colors duration-300" data-id="element-662" data-mp="qanq3hro496t" data-mpi={i}>
                <div className="text-3xl md:text-4xl font-black text-brand-yellow leading-none mb-2" data-id="element-663" data-mp="1020hk89k0xcb">
                  {'text' in stat ? stat.text : <Counter end={stat.value} suffix={stat.suffix} data-id="element-664" data-mp="7htmfjzhw3hw" />}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide" data-id="element-665" data-mp="296pr8lsyswbx">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </FadeIn>

        <FadeIn delay={80} data-id="element-666" data-mp="2j0pxiqzzg7m8">
          <div className="space-y-6 text-lg text-gray-300 leading-[1.8]" data-id="element-667" data-mp="2e7n7gsuezgas">
            {en ? <>
                <p data-id="element-668" data-mp="2sw047s1bt36c">
                  D-TECH specializes in the import and sale of professional
                  vertical cargo transport equipment, with a focus on safety,
                  reliability and ease of use.
                </p>
                <p data-id="element-669" data-mp="31vn3a76l1ww8">
                  Our goal is to enable companies and individuals to handle
                  cargo more efficiently while reducing physical strain,
                  increasing safety and improving everyday work processes.
                </p>
                <p data-id="element-670" data-mp="12lx4hhv3pqzb">
                  We work directly with manufacturers to ensure proven
                  equipment, technical support and spare parts. Models available
                  from stock can be delivered the same day, while models made to
                  order arrive within 4 to 6 weeks, depending on the model and
                  the quantity ordered.
                </p>
                <p data-id="element-671" data-mp="39g77vbugxv0s">
                  We provide technical support, servicing and access to genuine
                  spare parts to ensure safe, long-term operation of the
                  equipment for our customers.
                </p>
              </> : ru ? <>
                <p data-id="element-672" data-mp="2mhuhjxp2pvug">
                  D-TECH занимается импортом и продажей профессионального
                  оборудования для вертикальной транспортировки грузов, уделяя
                  особое внимание безопасности, надёжности и простоте
                  использования.
                </p>
                <p data-id="element-673" data-mp="opzzwb2egb4e">
                  Наша цель — позволить компаниям и частным лицам эффективнее
                  обращаться с грузами, снижая физическую нагрузку, повышая
                  безопасность и улучшая повседневные рабочие процессы.
                </p>
                <p data-id="element-674" data-mp="1on9y2wyeoezn">
                  Мы работаем напрямую с производителями, чтобы обеспечить
                  проверенное оборудование, техническую поддержку и запасные
                  части. Модели, имеющиеся на складе, доступны к отправке в тот
                  же день, а модели под заказ поставляются в течение 4–6 недель
                  в зависимости от модели и заказанного количества.
                </p>
                <p data-id="element-675" data-mp="27lme92nqip4q">
                  Мы предоставляем техническую поддержку, сервис и доступ к
                  оригинальным запасным частям, чтобы обеспечить безопасную и
                  долговременную работу оборудования для наших клиентов.
                </p>
              </> : <>
                <p data-id="element-676" data-mp="2vayhzn0am4zn">
                  D-TECH se bavi uvozom i prodajom profesionalne opreme za
                  vertikalni transport tereta, sa fokusom na bezbednost,
                  pouzdanost i jednostavnu upotrebu.
                </p>
                <p data-id="element-677" data-mp="37nsshh5ovds4">
                  Naš cilj je da kompanijama i pojedincima omogućimo efikasnije
                  rukovanje teretom uz smanjenje fizičkog opterećenja, povećanje
                  bezbednosti i unapređenje svakodnevnih radnih procesa.
                </p>
                <p data-id="element-678" data-mp="3ll7l0ur8o1ob">
                  Sarađujemo direktno sa proizvođačima kako bismo obezbedili
                  proverenu opremu, tehničku podršku i rezervne delove. Modeli
                  koji su na lageru mogu se isporučiti istog dana, dok se modeli
                  po narudžbini isporučuju u roku od 4 do 6 nedelja, u
                  zavisnosti od modela i naručene količine.
                </p>
                <p data-id="element-679" data-mp="88nqdy4ny4oh">
                  Pružamo tehničku podršku, servis i pristup originalnim
                  rezervnim delovima kako bismo korisnicima obezbedili siguran i
                  dugoročan rad opreme.
                </p>
              </>}
          </div>
        </FadeIn>
      </div>
    </main>;
}